import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
} from '@mui/material';
import { FormikHandlers, FormikHelpers, FormikState } from 'formik';
import React, { useMemo } from 'react';

// Type for the props of the FormikCheckboxGroup component
interface FormikCheckboxGroupProps<
  T extends object,
  K extends keyof T & string,
> {
  formik: FormikState<T> & FormikHandlers & FormikHelpers<T>;
  name: K;
  options: Record<string, string>;
  helpText?: string;
  disabled?: boolean;
  dataTestId?: string;
}

/**
 * A generic Formik component for rendering a group of checkboxes
 */
const FormikCheckboxGroup = <
  T extends Record<K, string[]>,
  K extends keyof T & string,
>(
  props: FormikCheckboxGroupProps<T, K>
) => {
  const { formik, name, options, helpText, disabled } = props;

  const errors = useMemo(() => {
    const formikErrors = formik.errors[name];
    return formikErrors as string | undefined;
  }, [formik, name]);

  const touched = useMemo(() => {
    return formik.touched[name];
  }, [formik, name]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const currentValues = formik.values[name];
    let updatedValues;

    if (currentValues.includes(value)) {
      updatedValues = currentValues.filter((item) => item !== value);
    } else {
      updatedValues = [...currentValues, value];
    }

    formik.setFieldValue(name, updatedValues);
  };

  return (
    <FormGroup data-testid={props.dataTestId}>
      {Object.keys(options).map((key) => (
        <FormControlLabel
          key={key}
          control={
            <Checkbox
              checked={formik.values[name].includes(key)}
              onChange={handleChange}
              value={key}
              disabled={formik.isSubmitting || disabled}
            />
          }
          label={options[key]}
        />
      ))}
      <FormHelperText error={Boolean(touched && errors)}>
        {(touched && errors)! ?? helpText}
      </FormHelperText>
    </FormGroup>
  );
};

export default FormikCheckboxGroup;
